import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Votestars from './Votestars';

export default function SongList({ songs, compact = false, showRequestButton = true, showTime = true }) {
  const { favorites, isFavorited, toggleFavorite, authToken, voteForSong, removeVoteForSong, votes } = useAuth();
  const [queuedSongs, setQueuedSongs] = useState([]);
  const [userVotes, setUserVotes] = useState({});

  // Initialize userVotes from global votes
  React.useEffect(() => {
    if (votes) setUserVotes(votes);
  }, [votes]);

  const handleVote = async (songId, vote) => {
    const currentVote = userVotes[songId];
    try {
      if (currentVote === vote) {
        await removeVoteForSong(songId);
        setUserVotes((prev) => {
          const updated = { ...prev };
          delete updated[songId];
          return updated;
        });
        toast.success('✅ Vote removed');
      } else {
        await voteForSong(songId, vote);
        setUserVotes((prev) => ({ ...prev, [songId]: vote }));
        toast.success(`✅ Voted ${vote}`);
      }
    } catch (err) {
      toast.error('❌ Failed to update vote');
      console.error('Vote error:', err);
    }
  };

  if (!songs || songs.length === 0) {
    return <p className="text-gray-500">No songs available.</p>;
  }

  const requestSong = async (songId) => {
    try {
      const res = await fetch(`/api/song/${songId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (res.status === 200) {
        toast.success('✅ Song successfully queued!');
        setQueuedSongs((prev) => [...prev, songId]);
      } else if (res.status === 429) {
        toast.error('⏱️ You’re queuing too fast. Wait a moment.');
      } else if (res.status === 403) {
        toast.error('⛔ You already have 2 songs in the queue.');
      } else if (res.status === 409) {
        toast.error('📻 This song was played recently.');
      } else {
        toast.error('❌ Unexpected server response.');
      }
    } catch {
      toast.error('❌ Failed to queue song');
    }
  };

  return (
    <ul className="space-y-3">
      {songs.map((song) => {
        const fav = isFavorited(song.id);
        const minutes = Math.floor(song.length / 60);
        const seconds = song.length % 60;
        const avgVote = song.vote_count > 0
          ? Math.round(song.total_vote_score / song.vote_count)
          : null;
        const userVote = song.user_vote || userVotes[song.id] || 0;

        const isDisabled = song.recently_played || queuedSongs.includes(song.id);

        const playTime = song.played_at || song.start_time;
        const now = Date.now();
        const timeDiff = playTime ? Math.floor((playTime * 1000 - now) / 60000) : null;
        let playTimeStr = '-';
        if (timeDiff !== null) {
          if (song.source === 'now') {
            playTimeStr = '▶ Now';
          } else if (song.source === 'upcoming') {
            if (timeDiff <= 1) {
              playTimeStr = 'Next up!';
            } else {
              playTimeStr = `in ${timeDiff} min${timeDiff === 1 ? '' : 's'}`;
            }
          } else if (song.source === 'history') {
            if (timeDiff >= 1) {
              playTimeStr = 'Just played';
            } else {
              const absDiff = Math.abs(timeDiff);
              playTimeStr = `${absDiff} min${absDiff === 1 ? '' : 's'} ago`;
            }
          }
        }

        return (
          <li key={`${song.id}-${song.start_time || song.played_at}`} className="p-3 border rounded shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                {song.artists
                  ? Object.entries(song.artists).map(([artistId, artistName], i, arr) => (
                      <span key={artistId}>
                        <Link to={`/artist/${artistId}`} className="text-blue-700 hover:underline">
                          {artistName}
                        </Link>
                        {i < arr.length - 1 && ', '}
                      </span>
                    ))
                  : <span className="text-gray-400">Unknown</span>
                }
                &nbsp;—&nbsp;
                <Link to={`/song/${song.id}`} className="text-blue-700 hover:underline">{song.title}</Link>
                <div className="text-sm text-gray-500">
                  {minutes}:{seconds.toString().padStart(2, '0')}
                  {showTime && <span className="ml-2">• {playTimeStr}</span>}
                  {song.requested_by_username && (
                    <span className="ml-2 italic text-blue-700">
                      requested by {song.requested_by_username}
                    </span>
                  )}
                </div>
                <div className="mt-1">
                  <Votestars
                    value={userVote}
                    onVote={(songId, vote) => handleVote(songId, vote)}
                    avg={avgVote}
                    songId={song.id}
                    disabled={!authToken} // Disable voting if not logged in
                  />
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => toggleFavorite(song.id)}
                  className="text-xl"
                  title={fav ? 'Unfavorite' : 'Favorite'}
                >
                  <img
                    src={fav ? '/emoji/2764.svg' : '/emoji/1F90D.svg'}
                    alt={fav ? 'Favorited' : 'Not favorited'}
                    className="inline w-5 h-5 align-text-bottom"
                    draggable={false}
                  />
                </button>
                {showRequestButton && (
                  <button
                    onClick={() => requestSong(song.id)}
                    disabled={isDisabled}
                    className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Request
                  </button>
                )}
              </div>
            </div>
            {showRequestButton && song.recently_played && (
              <p className="text-sm text-yellow-700 mt-1">
                🔒 This song was played recently and can’t be queued again just yet.
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
